import type {
  AuthKind,
  ConnectedAccount,
  Connection,
  ConnectorSource,
  MarketplaceListing,
  ToolCategory,
} from '../api/types'
import type { BadgeTone } from '../components/ui'

export type AuthType = 'OAuth2' | 'API Key' | 'No auth'

/**
 * How a toolkit gets its credential, which decides the connect flow. Read straight from
 * the listing's linked connector (`auth_kind` + `source`, GET /marketplace):
 *   oauth        — auth_kind oauth (any source): per-user connected account via
 *                  GET /oauth/{connect_provider}/authorize
 *   upstream_key — auth_kind api_key + source mcp_upstream: per-user API key / bearer
 *                  token, PUT /connected-accounts/{connect_provider}/api-key
 *   secrets      — auth_kind api_key + source builtin/openapi: workspace secrets on a
 *                  server row, POST /servers → PUT secrets → POST activate
 *   none         — auth_kind none: POST /servers enables it
 */
export type ConnectKind = 'oauth' | 'upstream_key' | 'secrets' | 'none'

export type ConnState =
  | 'connected'
  | 'needs_reconnect'
  | 'pending_config'
  | 'deploying'
  | 'unhealthy'
  | 'inactive'
  | 'not_connected'

export const STATE_BADGE: Record<ConnState, { tone: BadgeTone; label: string }> = {
  connected: { tone: 'success', label: 'Connected' },
  needs_reconnect: { tone: 'warning', label: 'Needs reconnect' },
  pending_config: { tone: 'warning', label: 'Setup incomplete' },
  deploying: { tone: 'info', label: 'Deploying' },
  unhealthy: { tone: 'error', label: 'Unhealthy' },
  inactive: { tone: 'neutral', label: 'Turned off' },
  not_connected: { tone: 'neutral', label: 'Not connected' },
}

export interface ToolkitRow {
  key: string
  name: string
  iconUrl: string | null
  slugs: string[]
  /** Tool-category slugs (GET /tool-categories vocabulary), 1–3 per connector. */
  categories: string[]
  provider: MarketplaceListing['provider']
  auth: AuthType[]
  kind: ConnectKind
  /** The linked connector's source (null when the listing links no connector). */
  source: ConnectorSource | null
  /** Provider name for /oauth/{provider} and /connected-accounts/{provider} (`connect_provider`). */
  providerName: string
  state: ConnState
  connected: boolean
  connection: Connection | null
  account: ConnectedAccount | null
  /** Secret key names the listing declares (the server row's list wins once it exists). */
  requiredSecrets: string[]
  installs: number
  updated: string | null
  description: string | null
}

export function listingCategories(l: MarketplaceListing): string[] {
  return l.categories ?? []
}

const KIND_AUTH: Record<ConnectKind, AuthType> = {
  oauth: 'OAuth2',
  upstream_key: 'API Key',
  secrets: 'API Key',
  none: 'No auth',
}

const SERVER_STATE: Record<Connection['status'], ConnState> = {
  active: 'connected',
  pending_config: 'pending_config',
  deploying: 'deploying',
  unhealthy: 'unhealthy',
  inactive: 'inactive',
}

/** The connect flow for a connector's `auth_kind` + `source` (null ⇒ no linked connector / not derivable). */
export function connectKindFor(
  authKind: AuthKind | null | undefined,
  source: ConnectorSource | null | undefined,
): ConnectKind | null {
  switch (authKind) {
    case 'oauth':
      return 'oauth'
    case 'api_key':
      return source === 'mcp_upstream' ? 'upstream_key' : 'secrets'
    case 'none':
      return 'none'
    default:
      return null
  }
}

/**
 * The toolkit's connect flow, from the first variant whose connector declares an
 * `auth_kind`. A listing with no derivable auth kind can still be enabled; if a server
 * row already exists and declares required secrets, the secrets flow finishes it.
 */
export function connectKindOf(listings: MarketplaceListing[], conn: Connection | undefined): ConnectKind {
  for (const l of listings) {
    const k = connectKindFor(l.auth_kind, l.source)
    if (k) return k
  }
  return (conn?.required_secrets?.length ?? 0) > 0 ? 'secrets' : 'none'
}

export function stateOf(kind: ConnectKind, conn: Connection | undefined, account: ConnectedAccount | undefined): ConnState {
  if (kind === 'oauth' || kind === 'upstream_key') {
    if (!account) return 'not_connected'
    return account.status === 'needs_reconnect' ? 'needs_reconnect' : 'connected'
  }
  if (!conn) return 'not_connected'
  return SERVER_STATE[conn.status] ?? 'not_connected'
}

/**
 * Listings that share a server_type are variants of one toolkit. Connection state comes
 * from GET /connections (workspace server rows) and GET /connected-accounts (the caller's
 * own OAuth / API-key accounts, keyed by the listing's `connect_provider`).
 */
export function buildToolkitRows(
  listings: MarketplaceListing[],
  connections: Connection[],
  accounts: ConnectedAccount[] = [],
): ToolkitRow[] {
  const connByType = new Map<string, Connection>()
  for (const c of connections) if (c.server_type) connByType.set(c.server_type, c)
  const acctByProvider = new Map<string, ConnectedAccount>()
  for (const a of accounts) acctByProvider.set(a.provider, a)

  const groups = new Map<string, MarketplaceListing[]>()
  for (const l of listings) {
    const k = l.server_type || l.slug
    groups.set(k, [...(groups.get(k) ?? []), l])
  }

  return [...groups.entries()].map(([k, ls]) => {
    const primary = ls[0]
    const conn = connByType.get(k)
    const providerName = ls.map((l) => l.connect_provider).find(Boolean) ?? k
    const account = acctByProvider.get(providerName)
    const kind = connectKindOf(ls, conn)

    const categories = [...new Set(ls.flatMap(listingCategories))]
    const requiredSecrets = [...new Set(ls.flatMap((l) => l.required_secrets ?? []))]
    const updated = ls.map((l) => l.updated_at ?? l.created_at).filter(Boolean).sort().at(-1) ?? null
    const state = stateOf(kind, conn, account)
    return {
      key: k,
      name: primary.display_name,
      iconUrl: ls.map((l) => l.icon_url).find(Boolean) ?? null,
      slugs: ls.map((l) => l.slug),
      categories,
      provider: primary.provider,
      auth: [KIND_AUTH[kind]],
      kind,
      source: ls.map((l) => l.source).find(Boolean) ?? null,
      providerName,
      state,
      connected: state === 'connected',
      connection: conn ?? null,
      account: account ?? null,
      requiredSecrets,
      installs: ls.reduce((n, l) => n + (l.install_count ?? 0), 0),
      updated,
      description: primary.description ?? null,
    }
  })
}

export type SortKey = 'name' | 'popular' | 'recent'

export interface ToolkitFilters {
  search: string
  sort: SortKey
  provider: 'all' | MarketplaceListing['provider']
  auth: 'all' | AuthType
  connectedOnly: boolean
  /** A tool-category slug, or null for every category. */
  category: string | null
}

export const DEFAULT_FILTERS: ToolkitFilters = {
  search: '',
  sort: 'name',
  provider: 'all',
  auth: 'all',
  connectedOnly: false,
  category: null,
}

export function applyFilters(rows: ToolkitRow[], f: ToolkitFilters): ToolkitRow[] {
  const q = f.search.trim().toLowerCase()
  const out = rows.filter(
    (r) =>
      (!q || r.name.toLowerCase().includes(q) || r.key.toLowerCase().includes(q) || r.slugs.some((s) => s.toLowerCase().includes(q))) &&
      (f.provider === 'all' || r.provider === f.provider) &&
      (f.auth === 'all' || r.auth.includes(f.auth)) &&
      (!f.connectedOnly || r.connected) &&
      (!f.category || r.categories.includes(f.category)),
  )
  const by: Record<SortKey, (a: ToolkitRow, b: ToolkitRow) => number> = {
    name: (a, b) => a.name.localeCompare(b.name),
    popular: (a, b) => b.installs - a.installs,
    recent: (a, b) => (b.updated ?? '').localeCompare(a.updated ?? ''),
  }
  return out.sort(by[f.sort])
}

export interface CategoryOption {
  slug: string
  label: string
  count: number
}

/**
 * Category filter options: the GET /tool-categories vocabulary in its display order
 * (only categories some toolkit carries), then any slug a listing uses that the
 * vocabulary doesn't know (shown as-is).
 */
export function categoryOptions(rows: ToolkitRow[], vocab: ToolCategory[]): CategoryOption[] {
  const counts = new Map<string, number>()
  for (const r of rows) for (const c of r.categories) counts.set(c, (counts.get(c) ?? 0) + 1)
  const known = [...vocab]
    .sort((a, b) => a.sort_order - b.sort_order)
    .filter((c) => counts.has(c.slug))
    .map((c) => ({ slug: c.slug, label: c.display_name, count: counts.get(c.slug) ?? 0 }))
  const vocabSlugs = new Set(vocab.map((c) => c.slug))
  const unknown = [...counts.entries()]
    .filter(([s]) => !vocabSlugs.has(s))
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([slug, count]) => ({ slug, label: slug, count }))
  return [...known, ...unknown]
}

export function categoryLabel(slug: string, vocab: ToolCategory[]): string {
  return vocab.find((c) => c.slug === slug)?.display_name ?? slug
}
